import { useState, useEffect, useCallback } from "react";

interface KKSCode { id: string; code: string; description: string; }

// ── Reusable code-table section ───────────────────────────────────────────────

function CodeSection({
  title, endpoint, codeLen,
}: {
  title: string;
  endpoint: string;
  codeLen: 2 | 3;
}) {
  const [codes, setCodes] = useState<KKSCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(true);

  // Add form
  const [newCode, setNewCode] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState("");

  // Inline edit
  const [editId, setEditId] = useState<string | null>(null);
  const [editCode, setEditCode] = useState("");
  const [editDesc, setEditDesc] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    const r = await fetch(endpoint);
    if (r.ok) setCodes(await r.json());
    setLoading(false);
  }, [endpoint]);

  useEffect(() => { load(); }, [load]);

  async function handleAdd(e: React.FormEvent) {
    e.preventDefault();
    setAdding(true); setAddError("");
    const r = await fetch(endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ code: newCode.toUpperCase(), description: newDesc }),
    });
    setAdding(false);
    if (r.ok) { setNewCode(""); setNewDesc(""); load(); }
    else { const j = await r.json(); setAddError(j.error ?? "Failed"); }
  }

  function startEdit(c: KKSCode) {
    setEditId(c.id); setEditCode(c.code); setEditDesc(c.description); setSaveError("");
  }

  async function handleSave(id: string) {
    setSaving(true); setSaveError("");
    const r = await fetch(`${endpoint}/${id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ code: editCode.toUpperCase(), description: editDesc }),
    });
    setSaving(false);
    if (r.ok) { setEditId(null); load(); }
    else { const j = await r.json(); setSaveError(j.error ?? "Failed"); }
  }

  async function handleDelete(id: string, code: string) {
    if (!confirm(`Delete code "${code}"?`)) return;
    const r = await fetch(`${endpoint}/${id}`, { method: "DELETE" });
    if (r.ok) load();
  }

  const inputClass = "px-2 py-1 border border-slate-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500";

  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 text-left"
      >
        <span className="text-sm font-semibold text-slate-700">{title}</span>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}
          strokeLinecap="round" strokeLinejoin="round"
          style={{ width: 16, height: 16, transform: open ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s" }}>
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>

      {open && (
        <div>
          {loading ? (
            <p className="px-4 py-4 text-sm text-slate-400">Loading…</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-white border-b border-slate-100 text-xs text-slate-500">
                <tr>
                  <th className="text-left px-4 py-2 w-24">Code</th>
                  <th className="text-left px-4 py-2">Description</th>
                  <th className="px-4 py-2 w-32"></th>
                </tr>
              </thead>
              <tbody>
                {codes.map((c) => (
                  <tr key={c.id} className="border-t border-slate-100">
                    {editId === c.id ? (
                      <>
                        <td className="px-3 py-2">
                          <input
                            value={editCode}
                            onChange={(e) => setEditCode(e.target.value.toUpperCase().slice(0, codeLen))}
                            maxLength={codeLen}
                            className={`${inputClass} w-16 font-mono uppercase`}
                          />
                        </td>
                        <td className="px-3 py-2">
                          <input
                            value={editDesc}
                            onChange={(e) => setEditDesc(e.target.value)}
                            className={`${inputClass} w-full`}
                          />
                          {saveError && <p className="text-xs text-red-600 mt-0.5">{saveError}</p>}
                        </td>
                        <td className="px-3 py-2 text-right space-x-2 whitespace-nowrap">
                          <button onClick={() => handleSave(c.id)} disabled={saving}
                            className="text-xs text-blue-600 hover:underline disabled:opacity-50">
                            {saving ? "Saving…" : "Save"}
                          </button>
                          <button onClick={() => setEditId(null)}
                            className="text-xs text-slate-400 hover:text-slate-600">Cancel</button>
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="px-4 py-2 font-mono font-medium text-slate-800">{c.code}</td>
                        <td className="px-4 py-2 text-slate-600">{c.description}</td>
                        <td className="px-4 py-2 text-right space-x-3 whitespace-nowrap">
                          <button onClick={() => startEdit(c)}
                            className="text-xs text-blue-600 hover:underline">Edit</button>
                          <button onClick={() => handleDelete(c.id, c.code)}
                            className="text-xs text-red-500 hover:underline">Delete</button>
                        </td>
                      </>
                    )}
                  </tr>
                ))}

                {/* Add new row */}
                <tr className="border-t border-slate-200 bg-slate-50">
                  <td className="px-3 py-2">
                    <input
                      value={newCode}
                      onChange={(e) => setNewCode(e.target.value.toUpperCase().slice(0, codeLen))}
                      maxLength={codeLen}
                      placeholder={`${codeLen === 3 ? "ABC" : "AB"}`}
                      className={`${inputClass} w-16 font-mono uppercase`}
                    />
                  </td>
                  <td className="px-3 py-2">
                    <input
                      value={newDesc}
                      onChange={(e) => setNewDesc(e.target.value)}
                      placeholder="Description"
                      className={`${inputClass} w-full`}
                    />
                    {addError && <p className="text-xs text-red-600 mt-0.5">{addError}</p>}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button
                      onClick={handleAdd as any}
                      disabled={adding || newCode.length !== codeLen || !newDesc.trim()}
                      className="text-xs px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-40"
                    >
                      {adding ? "Adding…" : "Add"}
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function KksCodesPage() {
  const [station, setStation] = useState("");
  const [unit, setUnit] = useState("");
  const [configSaving, setConfigSaving] = useState(false);
  const [configMsg, setConfigMsg] = useState("");

  useEffect(() => {
    fetch("/api/kks/config")
      .then((r) => r.ok ? r.json() : null)
      .then((d) => { if (d) { setStation(d.station); setUnit(d.unit); } });
  }, []);

  async function saveConfig(e: React.FormEvent) {
    e.preventDefault();
    setConfigSaving(true); setConfigMsg("");
    const r = await fetch("/api/kks/config", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ station, unit }),
    });
    setConfigSaving(false);
    if (r.ok) setConfigMsg("Saved.");
    else setConfigMsg("Failed to save.");
    setTimeout(() => setConfigMsg(""), 2000);
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-slate-900">KKS Codes</h1>
        <p className="text-sm text-slate-500 mt-0.5">
          Manage KKS lookup tables and site defaults. Changes sync to all connected Pi nodes.
        </p>
      </div>

      {/* Site defaults */}
      <div className="bg-white border border-slate-200 rounded-lg p-5 mb-6">
        <h2 className="text-sm font-semibold text-slate-700 mb-4">Site Defaults</h2>
        <form onSubmit={saveConfig} className="flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Default Station (3 letters)</label>
            <input
              value={station}
              onChange={(e) => setStation(e.target.value.toUpperCase().replace(/[^A-Z]/g, "").slice(0, 3))}
              maxLength={3}
              placeholder="e.g. THI"
              className="px-3 py-2 border border-slate-300 rounded-md text-sm font-mono uppercase focus:outline-none focus:ring-2 focus:ring-blue-500 w-28"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Default Unit (1 digit)</label>
            <input
              value={unit}
              onChange={(e) => setUnit(e.target.value.replace(/[^0-9]/g, "").slice(0, 1))}
              maxLength={1}
              placeholder="e.g. 1"
              className="px-3 py-2 border border-slate-300 rounded-md text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500 w-20"
            />
          </div>
          <div className="flex items-center gap-3">
            <button type="submit" disabled={configSaving}
              className="px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 disabled:opacity-50">
              {configSaving ? "Saving…" : "Save Defaults"}
            </button>
            {configMsg && <span className="text-sm text-green-600">{configMsg}</span>}
          </div>
        </form>
        <p className="text-xs text-slate-400 mt-3">
          These pre-fill the Station and Unit fields when registering a fudge. Engineers can override them per fudge.
        </p>
      </div>

      {/* Lookup tables */}
      <div className="space-y-4">
        <CodeSection
          title="System Codes (Level 1 — 3 letters)"
          endpoint="/api/kks/system-codes"
          codeLen={3}
        />
        <CodeSection
          title="Equipment Unit Codes (Level 2 — 2 letters)"
          endpoint="/api/kks/equipment-unit-codes"
          codeLen={2}
        />
        <CodeSection
          title="Component Codes (Level 3 — 2 letters)"
          endpoint="/api/kks/component-codes"
          codeLen={2}
        />
      </div>
    </div>
  );
}
